/**
 * Compact activity timeline for negotiations (320px task pane).
 * Shows recent events: version uploads, party changes, comment resolutions, etc.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  GitBranch,
  Users,
  MessageSquare,
  Flag,
  PenTool,
  Settings,
  ChevronDown,
  ChevronUp,
  Activity,
} from 'lucide-react'
import { useNegotiationActivity } from '../../hooks/useNegotiationActivity'
import type { NegotiationActivityEntry } from '../../hooks/useNegotiationActivity'

const CATEGORY_ICONS: Record<string, typeof Activity> = {
  version: GitBranch,
  party: Users,
  comment: MessageSquare,
  status: Flag,
  signing: PenTool,
  system: Settings,
}

const CATEGORY_COLORS: Record<string, string> = {
  version: 'bg-primary-500',
  party: 'bg-blue-500',
  comment: 'bg-amber-500',
  status: 'bg-purple-500',
  signing: 'bg-success-500',
  system: 'bg-neutral-400',
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / (1000 * 60))
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

interface NegotiationActivityLogProps {
  negotiationId: number
  alwaysExpanded?: boolean
}

export function NegotiationActivityLog({ negotiationId, alwaysExpanded }: NegotiationActivityLogProps) {
  const { t } = useTranslation()
  const { data: entries, isLoading } = useNegotiationActivity(negotiationId, 30)
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded ?? false)

  const expanded = alwaysExpanded || isExpanded

  return (
    <div className={alwaysExpanded ? '' : 'border-b border-neutral-100 dark:border-neutral-800'}>
      {!alwaysExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        >
          <Activity size={10} />
          {t('negotiate.activity', 'Activity')}
          {entries && (
            <span className="text-[10px] text-neutral-400 normal-case">
              ({entries.length})
            </span>
          )}
          <span className="flex-1" />
          {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
      )}

      {expanded && (
        <div className="px-3 pb-2">
          {isLoading && (
            <div className="space-y-2 py-1">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-start gap-2 animate-pulse">
                  <div className="w-3 h-3 rounded-full bg-neutral-200 dark:bg-neutral-700 shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-1">
                    <div className="h-2.5 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
                    <div className="h-2 bg-neutral-100 dark:bg-neutral-800 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && (!entries || entries.length === 0) && (
            <p className="text-[10px] text-neutral-400 py-2 text-center">
              {t('negotiate.noActivity', 'No activity yet')}
            </p>
          )}

          {!isLoading && entries && entries.length > 0 && (
            <div className="relative ml-1.5 py-1">
              {/* Timeline line */}
              <div className="absolute left-0 top-2 bottom-2 w-px bg-neutral-200 dark:bg-neutral-700" />

              {entries.map((entry, index) => (
                <ActivityItem key={entry.id} entry={entry} isFirst={index === 0} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ActivityItem({ entry, isFirst }: { entry: NegotiationActivityEntry; isFirst: boolean }) {
  const Icon = CATEGORY_ICONS[entry.category] || Activity
  const dotColor = CATEGORY_COLORS[entry.category] || 'bg-neutral-400'
  const performer = entry.performed_by_system
    ? 'System'
    : entry.performed_by_name || entry.performed_by_display?.display_name || ''

  return (
    <div className="relative flex items-start gap-2 py-1">
      {/* Timeline dot */}
      <div
        className={`relative z-10 w-3 h-3 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
          isFirst ? dotColor : 'bg-neutral-300 dark:bg-neutral-600'
        }`}
        style={{ marginLeft: '-6px' }}
      >
        {isFirst && <Icon size={7} className="text-white" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex items-baseline justify-between gap-1">
        <div className="min-w-0">
          <p className="text-[10px] text-neutral-700 dark:text-neutral-300 truncate">
            {entry.description}
          </p>
          {performer && (
            <span className="text-[9px] text-neutral-400 truncate block">
              {performer}
            </span>
          )}
        </div>
        <span className="text-[9px] text-neutral-400 shrink-0">
          {formatRelativeTime(entry.created_at)}
        </span>
      </div>
    </div>
  )
}
